import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import EventBlock, {
  AllDayChip,
  gridHour,
  hm,
} from "./calendar/EventBlock";
import { Skeleton } from "./LoadingSkeleton";
import { IconChevronLeft, IconChevronRight, IconCollapse } from "./icons";
import { useAuth } from "../hooks/useAuth";
import { getCalendarDay } from "../lib/api";
import { layoutDay, nowMinutes, weekLabel } from "../lib/agenda";
import { getCachedDay, setCachedDay, subscribeDayCache } from "../lib/dayCache";
import { shiftIsoDay, startOfWeek, weekDays } from "../lib/day";
import { weekdayShort } from "../lib/format";
import type { CalendarDay, CalendarEvent } from "../types";

/**
 * «Мій день» at full size — Monday to Sunday, the same hour grid, over the
 * whole screen.
 *
 * The card answers "what is today"; a week answers "when is there room", and
 * that question cannot be asked of a 5/12 column one day at a time. It is an
 * overlay rather than a route on purpose: he opens it to look, not to navigate,
 * and a page he has to come back from is a worse door than one he closes with
 * Escape. The container transform is what says so — the panel grows out of the
 * exact card he pressed, so the week reads as that card opened rather than as a
 * new screen that replaced it.
 *
 * Nothing here changes the card behind it. The week starts on the day the card
 * is showing and walks by weeks of its own; closing puts him back exactly where
 * he was, on the day he was on.
 */

/**
 * One hour of the week grid — taller than the card's 40px because there are
 * seven columns to read across and the eye needs the extra row height to keep
 * a line of blocks together.
 */
const WEEK_HOUR_PX = 52;

/** The hour gutter, in the `w-11` the card already uses for its own. */
const GUTTER = "w-11";

/**
 * Below `md` seven columns cannot both be readable and fit: a day narrower
 * than ~7rem cannot hold a meeting title at all. So the body keeps a floor of
 * gutter + 7 × 7rem = 51.75rem and scrolls sideways under the thumb, with the
 * hour gutter pinned to the left edge; from `md` up the floor is dropped and
 * the columns are fluid sevenths.
 */
const MIN_BODY = "min-w-[51.75rem] md:min-w-full";

/** Where the shimmer sits in a column still on its way, in hours from the top. */
const SKELETON_BLOCKS: [number, number][] = [
  [1, 1],
  [4, 1.5],
];

const OPEN_MS = 260;
const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";

/** The one strip above the grid that says why the week is not the whole truth. */
const NOTICE =
  "flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-3 py-2 text-xs sm:px-4";

/** What a day says when it has no calendar and does not say why. */
const NO_CALENDAR = "Календар не підключено.";

interface DayState {
  events: CalendarEvent[];
  /** False when Google refused this day — {@link DayState.note} says why. */
  available: boolean;
  /** `meta.note`: Google's own sentence, shown instead of an invented one. */
  note: string | null;
  /** Nothing to draw yet. A day already drawn never goes back to this. */
  loading: boolean;
  failed: boolean;
  /** An answer is on screen — from the cache or from Google. */
  loaded: boolean;
}

const EMPTY: DayState = {
  events: [],
  available: true,
  note: null,
  loading: false,
  failed: false,
  loaded: false,
};

/**
 * A day as it must look while it is being asked for.
 *
 * The shimmer belongs to a column with nothing in it. A day already drawn from
 * the cache is re-asked *in silence* and keeps its blocks until the answer
 * lands — the card's own rule (`DayTimeline`: `setLoading(!cached)`), because
 * a calendar that blanks itself to prove it is current is worse than one that
 * is ten minutes behind for a second.
 */
export function asking(prev: DayState | undefined): DayState {
  const base = prev ?? EMPTY;
  return { ...base, loading: !base.loaded, failed: false };
}

/** A fetched (or cached) day, as the column that draws it. */
export function answered(day: CalendarDay): DayState {
  return {
    events: day.events,
    available: day.available,
    note: day.meta.note,
    loading: false,
    failed: false,
    loaded: true,
  };
}

/**
 * What the week says when Google refused it, instead of «жодної події».
 *
 * `available: false` is a 200 with a note — a revoked token, a calendar never
 * connected. Nothing failed and nothing is loading, so without this the grid
 * would report an empty week: a verdict on days it was never shown.
 */
export function calendarNote(columns: DayState[]): string | null {
  const refused = columns.filter((c) => c.loaded && !c.available);
  if (refused.length === 0) return null;
  return refused.find((c) => c.note)?.note ?? NO_CALENDAR;
}

interface WeekCalendarProps {
  /** The day the card is standing on — the week shown is the one containing it. */
  day: string;
  /** The summary's own today (Kyiv): the highlighted column and the «зараз» line. */
  today: string;
  /** `context.calendar_today` — today's column, already on the page. */
  todayEvents: CalendarEvent[];
  /** False when Google is unconfigured or unauthorized, for `today` only. */
  todayAvailable: boolean;
  /** `context.calendar_note` — why today has no calendar, in Google's words. */
  todayNote: string | null;
  /**
   * The card's `getBoundingClientRect()` at the moment the button was pressed:
   * where the panel grows from, and where it collapses back to. Null falls the
   * animation back to a plain fade.
   */
  from: DOMRect | null;
  /** Called once the closing animation has finished, so the panel unmounts clean. */
  onClose: () => void;
}

export default function WeekCalendar({
  day,
  today,
  todayEvents,
  todayAvailable,
  todayNote,
  from: sourceRect,
  onClose,
}: WeekCalendarProps) {
  const userId = useAuth().user?.id ?? "";
  const [monday, setMonday] = useState(() => startOfWeek(day));
  const days = useMemo(() => weekDays(monday), [monday]);

  const [state, setState] = useState<Record<string, DayState>>({});
  /** Bumped per week, so answers to a week he has already left are dropped. */
  const weekRef = useRef(0);
  /** Bumped by «Оновити» on the page behind, which empties the day cache. */
  const [reload, setReload] = useState(0);

  const load = useCallback((wanted: string[], uid: string) => {
    if (wanted.length === 0) return;
    const generation = weekRef.current;
    setState((prev) => {
      const next = { ...prev };
      for (const d of wanted) next[d] = asking(next[d]);
      return next;
    });
    for (const d of wanted) {
      getCalendarDay(d)
        .then((answer) => {
          setCachedDay(uid, d, answer);
          if (weekRef.current !== generation) return;
          setState((prev) => ({ ...prev, [d]: answered(answer) }));
        })
        .catch(() => {
          if (weekRef.current !== generation) return;
          setState((prev) => ({
            ...prev,
            [d]: { ...(prev[d] ?? EMPTY), loading: false, failed: true },
          }));
        });
    }
  }, []);

  // Seven days at once, each from the cache when it is there. A stale day is
  // still drawn immediately and re-asked behind the render: a calendar that
  // blanks itself to prove it is current is worse than one ten minutes behind
  // for a second. Today never costs a request — the page is already holding it.
  useEffect(() => {
    weekRef.current += 1;
    const seeded: Record<string, DayState> = {};
    const wanted: string[] = [];
    for (const d of weekDays(monday)) {
      if (d === today) continue;
      const cached = getCachedDay(userId, d);
      if (cached) {
        seeded[d] = answered(cached.value);
        if (cached.stale) wanted.push(d);
      } else {
        wanted.push(d);
      }
    }
    setState(seeded);
    load(wanted, userId);
  }, [monday, today, userId, reload, load]);

  useEffect(() => subscribeDayCache(() => setReload((n) => n + 1)), []);

  const column = useCallback(
    (d: string): DayState =>
      d === today
        ? {
            events: todayEvents,
            available: todayAvailable,
            note: todayNote,
            loading: false,
            failed: false,
            loaded: true,
          }
        : (state[d] ?? EMPTY),
    [state, today, todayAvailable, todayEvents, todayNote],
  );

  const [nowMin, setNowMin] = useState(nowMinutes);
  useEffect(() => {
    const timer = window.setInterval(() => setNowMin(nowMinutes()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // Every column shares one vertical window: the union of what each day needs.
  // Seven grids with seven different 08:00s would make a row of blocks at the
  // same height mean seven different hours, which is the one thing a week view
  // exists to prevent.
  const columns = useMemo(() => days.map(column), [days, column]);
  const layouts = useMemo(
    () =>
      columns.map((col, i) =>
        layoutDay(col.events, days[i] === today ? nowMin : null),
      ),
    [columns, days, today, nowMin],
  );
  const gridFrom = Math.min(...layouts.map((l) => l.from));
  const gridTo = Math.max(...layouts.map((l) => l.to));
  const height = ((gridTo - gridFrom) / 60) * WEEK_HOUR_PX;
  const y = (min: number): number => ((min - gridFrom) / 60) * WEEK_HOUR_PX;

  const hours: number[] = [];
  for (let t = gridFrom; t < gridTo; t += 60) hours.push(t);

  const hasToday = days.includes(today);
  const nowVisible = hasToday && nowMin >= gridFrom && nowMin <= gridTo;
  // The «зараз» pill owns the gutter where it stands: two times printed on top
  // of each other read as neither.
  const hidesHourLabel = (hour: number): boolean =>
    nowVisible && Math.abs(y(hour) - y(nowMin)) < WEEK_HOUR_PX / 2;

  const failed = days.filter((d) => state[d]?.failed);
  // `loading` is true only for a column with nothing in it (see `asking`), so a
  // week refreshing behind its own blocks neither shimmers nor holds the scroll
  // below — which is the whole point of drawing a stale day at once.
  const loadingAny = days.some((d) => state[d]?.loading);
  const hasAllDay = layouts.some((l) => l.allDay.length > 0);
  const noCalendar = calendarNote(columns);
  const emptyWeek =
    !loadingAny &&
    failed.length === 0 &&
    noCalendar === null &&
    layouts.every((l) => l.blocks.length === 0 && l.allDay.length === 0);

  /* -------------------------------------------------------- scroll position */

  const scrollRef = useRef<HTMLDivElement>(null);
  const nowRef = useRef(nowMin);
  nowRef.current = nowMin;
  const firstBlockStart = useMemo(() => {
    const starts = layouts.flatMap((l) => l.blocks.map((b) => b.start));
    return starts.length > 0 ? Math.min(...starts) : null;
  }, [layouts]);

  // Same rule as the card: the useful part of the week is where he is now, and
  // on any other week it is that week's first event. A number, not the layout
  // object, so the minute tick cannot re-run this and fight his own scrolling.
  useEffect(() => {
    const box = scrollRef.current;
    if (!box || loadingAny) return;
    const anchor = hasToday ? nowRef.current : firstBlockStart;
    if (anchor === null) return;
    box.scrollTop = Math.max(
      0,
      ((anchor - gridFrom) / 60) * WEEK_HOUR_PX - box.clientHeight / 3,
    );
  }, [monday, hasToday, loadingAny, gridFrom, firstBlockStart]);

  /* ------------------------------------------------------------- the panel */

  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const closingRef = useRef(false);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const ms = motionMs();
    const [start, end] = panelFrames(sourceRect, panel.getBoundingClientRect());
    run(backdropRef.current, [{ opacity: 0 }, { opacity: 1 }], ms, "linear");
    run(panel, [start, end], ms, EASE);
    // The content fades over the second half only. During the first half the
    // panel is still card-shaped and card-sized, and a week grid squeezed into
    // that is a smear — better it simply is not there yet.
    if (sourceRect) run(contentRef.current, FADE_IN, ms, "linear");
    closeRef.current?.focus();
    // Runs once: this is the entrance, and `sourceRect` is the rect it started
    // from, captured before the panel existed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    const panel = panelRef.current;
    const ms = motionMs();
    // Measure the panel where it *rests*, not where a half-finished entrance
    // has it: closing during the opening animation would otherwise collapse
    // towards a rect that never existed.
    cancelAnimations(panel);
    const rect = panel?.getBoundingClientRect() ?? null;
    const [start, end] = panelFrames(sourceRect, rect);
    const done = [
      run(backdropRef.current, [{ opacity: 1 }, { opacity: 0 }], ms, "linear", "forwards"),
      run(panel, [end, start], ms, EASE, "forwards"),
      sourceRect
        ? run(contentRef.current, FADE_OUT, ms, "linear", "forwards")
        : Promise.resolve(),
    ];
    void Promise.all(done).then(onClose, onClose);
  }, [onClose, sourceRect]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        requestClose();
      } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        // Without this the arrows also scroll the week sideways, so one press
        // would both change the week and slide the one it landed on.
        event.preventDefault();
        setMonday((m) => shiftIsoDay(m, event.key === "ArrowLeft" ? -7 : 7));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  const isCurrentWeek = monday === startOfWeek(today);

  return createPortal(
    <div className="fixed inset-0 z-modal">
      <button
        ref={backdropRef}
        type="button"
        tabIndex={-1}
        aria-label="Закрити"
        onClick={requestClose}
        className="absolute inset-0 h-full w-full bg-bg/70 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Календар на тиждень"
        className="jv-elevated absolute inset-[2.5%] flex origin-top-left flex-col overflow-hidden shadow-3"
      >
        <div ref={contentRef} className="flex min-h-0 flex-1 flex-col">
          <header className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2.5 sm:px-4">
            <div className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                className="jv-btn px-1.5"
                aria-label="Попередній тиждень"
                onClick={() => setMonday(shiftIsoDay(monday, -7))}
              >
                <IconChevronLeft className="size-4" />
              </button>
              <button
                type="button"
                className="jv-btn px-1.5"
                aria-label="Наступний тиждень"
                onClick={() => setMonday(shiftIsoDay(monday, 7))}
              >
                <IconChevronRight className="size-4" />
              </button>
            </div>

            <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">
              {weekLabel(monday, today)}
            </h2>

            {/* A button that returns him to the week he is already on changes
                nothing on screen, so on this week there is no button. */}
            {!isCurrentWeek && (
              <button
                type="button"
                className="jv-btn shrink-0"
                onClick={() => {
                  setMonday(startOfWeek(today));
                  // This press is also this button's last: on the current week
                  // it is gone, and focus would fall to <body> with it.
                  closeRef.current?.focus();
                }}
              >
                Сьогодні
              </button>
            )}

            <button
              ref={closeRef}
              type="button"
              className="jv-btn shrink-0"
              aria-label="Згорнути"
              title="Згорнути"
              onClick={requestClose}
            >
              <IconCollapse className="size-4" />
              <span className="hidden sm:inline">Згорнути</span>
            </button>
          </header>

          {/* Two things can stand between him and a week, and they are not the
              same thing. A day that *failed* is worth shouting about and worth
              a retry. A day Google *refused* is not an error at all — it is a
              200 saying there is no calendar, and the answer to it is Google's
              own sentence, in the card's own tone (`DayTimeline`), with no
              button: pressing it again would fetch the same refusal. */}
          {failed.length > 0 ? (
            <div role="alert" className={`${NOTICE} bg-danger/8 text-danger`}>
              <span>
                Не вдалося завантажити{" "}
                <span className="jv-num">{failed.length}</span> дн.
              </span>
              <button
                type="button"
                className="jv-btn"
                onClick={() => load(failed, userId)}
              >
                Спробувати ще раз
              </button>
            </div>
          ) : (
            noCalendar && (
              <div role="status" className={`${NOTICE} text-muted`}>
                {noCalendar}
              </div>
            )
          )}

          <div
            ref={scrollRef}
            className="min-h-0 flex-1 overflow-auto overscroll-contain"
          >
            <div className={MIN_BODY}>
              {/* Пн 8 … Нд 14. Sticky at the top so the column he is reading
                  never loses its date, and the gutter sticky at the left so it
                  never loses its hour.

                  Two tiers, both off the semantic scale (§9): the gutter floats
                  over the columns, the date row floats over both. They cannot
                  share a tier — the hour labels sit *later* in the document, so
                  at equal z they would paint straight over the pinned dates.
                  Nothing escapes the panel: the `z-modal` wrapper around it is
                  its own stacking context. */}
              <div className="sticky top-0 z-dropdown flex border-b border-line bg-elev">
                <div
                  className={`sticky left-0 z-sticky ${GUTTER} shrink-0 bg-elev`}
                />
                <div className="grid flex-1 grid-cols-7">
                  {days.map((d) => (
                    <div
                      key={d}
                      className="flex min-w-0 items-center justify-center gap-1.5 border-l border-line px-1 py-2"
                    >
                      <span className="text-micro text-muted">
                        {weekdayShort(d)}
                      </span>
                      <span
                        className={`jv-num inline-flex size-5 shrink-0 items-center justify-center rounded-full text-micro ${
                          d === today
                            ? "bg-accent font-medium text-bg"
                            : "text-fg"
                        }`}
                      >
                        {dayNumber(d)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {hasAllDay && (
                <div className="flex border-b border-line">
                  <div
                    className={`sticky left-0 z-sticky ${GUTTER} shrink-0 bg-elev`}
                  />
                  <div className="grid flex-1 grid-cols-7">
                    {days.map((d, i) => (
                      <div
                        key={d}
                        className="flex min-w-0 flex-col gap-1 border-l border-line p-1"
                      >
                        {layouts[i].allDay.map((event) => (
                          <AllDayChip key={event.id} event={event} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex pt-2 pb-1">
                <div
                  className={`sticky left-0 z-sticky ${GUTTER} shrink-0 bg-elev`}
                >
                  <div className="relative" style={{ height }}>
                    {[...hours, gridTo].map((hour) =>
                      hidesHourLabel(hour) ? null : (
                        <span
                          key={hour}
                          className="jv-num absolute right-1 -translate-y-1/2 text-micro text-muted"
                          style={{ top: y(hour) }}
                        >
                          {gridHour(hour)}
                        </span>
                      ),
                    )}
                    {nowVisible && (
                      <span
                        className="jv-num absolute left-0 -translate-y-1/2 rounded-full bg-accent px-1 text-micro font-medium text-bg"
                        style={{ top: y(nowMin) }}
                      >
                        {hm(nowMin)}
                      </span>
                    )}
                  </div>
                </div>

                <div
                  className="relative grid flex-1 grid-cols-7"
                  style={{ height }}
                >
                  {[...hours, gridTo].map((hour) => (
                    <span
                      key={`line-${hour}`}
                      className="pointer-events-none absolute inset-x-0 border-t border-line"
                      style={{ top: y(hour) }}
                      aria-hidden="true"
                    />
                  ))}

                  {days.map((d, i) => {
                    const col = columns[i];
                    return (
                      <div key={d} className="relative border-l border-line">
                        {col.loading
                          ? SKELETON_BLOCKS.map(([top, tall], n) => (
                              <Skeleton
                                key={n}
                                className="absolute inset-x-0.5"
                                style={{
                                  top: top * WEEK_HOUR_PX,
                                  height: tall * WEEK_HOUR_PX,
                                }}
                              />
                            ))
                          : layouts[i].blocks.map((block) => (
                              <EventBlock
                                key={block.event.id}
                                block={block}
                                from={gridFrom}
                                hourPx={WEEK_HOUR_PX}
                                narrow
                              />
                            ))}

                        {d === today && nowVisible && (
                          <span
                            className="pointer-events-none absolute inset-x-0 h-px bg-accent"
                            style={{ top: y(nowMin) }}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    );
                  })}

                  {emptyWeek && (
                    <p
                      role="status"
                      className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-muted"
                    >
                      Жодної події цього тижня
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** «8» — the date alone; the weekday beside it already says which day it is. */
const dayNumber = (day: string): string => String(Number(day.slice(8, 10)));

/* --------------------------------------------------------------- the motion */

/**
 * The content is absent for the first half and arrives over the second.
 *
 * Declared as keyframes rather than a CSS transition because the global
 * `prefers-reduced-motion` rule in `index.css` zeroes CSS durations only — the
 * Web Animations API does not see it, so {@link motionMs} asks the media query
 * itself.
 */
const FADE_IN: Keyframe[] = [
  { opacity: 0, offset: 0 },
  { opacity: 0, offset: 0.5 },
  { opacity: 1, offset: 1 },
];

const FADE_OUT: Keyframe[] = [
  { opacity: 1, offset: 0 },
  { opacity: 0, offset: 0.5 },
  { opacity: 0, offset: 1 },
];

const motionMs = (): number =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? 0
    : OPEN_MS;

/**
 * The container transform, as the two ends of one animation: the panel drawn
 * at its final size, scaled and shifted back onto the card that opened it.
 *
 * `transform-origin: top left` is what makes the arithmetic this short — the
 * scale multiplies outwards from the corner the translation already put in
 * place. Without a source rect (a card scrolled out of view, a keyboard press
 * with no geometry) there is nothing to grow from, so it falls back to the
 * system's ordinary dialog entrance.
 */
function panelFrames(from: DOMRect | null, panel: DOMRect | null): Keyframe[] {
  if (!from || !panel || panel.width === 0 || panel.height === 0) {
    return [
      { opacity: 0, transform: "scale(0.96)" },
      { opacity: 1, transform: "none" },
    ];
  }
  const sx = from.width / panel.width;
  const sy = from.height / panel.height;
  const dx = from.left - panel.left;
  const dy = from.top - panel.top;
  return [
    { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` },
    { transform: "none" },
  ];
}

function run(
  element: Element | null,
  keyframes: Keyframe[],
  ms: number,
  easing: string,
  fill: FillMode = "none",
): Promise<void> {
  if (!element || typeof element.animate !== "function") {
    return Promise.resolve();
  }
  try {
    const animation = element.animate(keyframes, {
      duration: ms,
      easing,
      fill,
    });
    return animation.finished.then(
      () => undefined,
      () => undefined,
    );
  } catch {
    return Promise.resolve();
  }
}

/** Stop the entrance mid-flight so the element can be measured where it rests. */
function cancelAnimations(element: Element | null): void {
  if (!element || typeof element.getAnimations !== "function") return;
  for (const animation of element.getAnimations()) animation.cancel();
}
